import React from 'react';
import classNames from 'classnames';
import I18n from 'i18n-js';

import { gql } from '@reverbdotcom/commons/src/gql';
import { useQuery } from '@reverbdotcom/commons/src/useQuery';
import PaypalLogo from '@reverbdotcom/cadence/images/vendor/paypal-logo.svg';
import CoreLink from '@reverbdotcom/commons/src/components/core_link';
import { I18N as Translate } from '@reverbdotcom/commons/src/components/translate';
import { RCIcon, RCImage } from '@reverbdotcom/cadence/components';
import { WarningIcon, ReverbLogoCircleIcon, BankIcon } from '@reverbdotcom/cadence/icons/react';
import {
  DashboardSellingOrderPayoutDetailsQuery,
  DashboardSellingOrderPayoutDetailsQueryVariables,
  core_apimessages_Payment_PaymentMethod,
  core_apimessages_Payment_PaymentStatus,
  PayoutDetailsStatus,
  PayoutDetailsDestination,
} from '@reverbdotcom/commons/src/gql/graphql';

import { Paths } from '../../../components/shared/url_helpers';
import { useExpEnabled } from '@reverbdotcom/commons/src/user_hooks';
import experiments from '@reverbdotcom/commons/src/experiments';

const shopPoliciesEditPath = Paths.shopPoliciesEdit.expand({});
const myEarningsPayoutsPath = Paths.myEarningsPayouts.expand({});
const howToGetPaidPaypalArticlePath = Paths.helpCenterArticlesUrl.expand({ articleId: '41988411420699' });
const howLongToGetPaidArticlePath = Paths.helpCenterArticlesUrl.expand({ articleId: '360002778613' });

const COMPLETED = 'completed';
const INCOMPLETE = 'incomplete';
const INCOMPLETE_PAYOUT = 'incompletePayout';

const normalizePayoutStatus = (
  payoutType: core_apimessages_Payment_PaymentMethod | PayoutDetailsDestination,
  payoutStatus: PayoutDetailsStatus,
  paymentStatus: core_apimessages_Payment_PaymentStatus,
  paypalPayoutsEnabled: boolean,
) => {
  if (payoutType === core_apimessages_Payment_PaymentMethod.PAYPAL) {
    if (paypalPayoutsEnabled) {
      return paymentStatus === core_apimessages_Payment_PaymentStatus.COMPLETED ? COMPLETED : INCOMPLETE_PAYOUT;
    }

    return paymentStatus === core_apimessages_Payment_PaymentStatus.COMPLETED ? COMPLETED : INCOMPLETE;
  } else {
    return payoutStatus === PayoutDetailsStatus.PAYMENT_INITIATED ? COMPLETED : INCOMPLETE;
  }
};

const CONTENT_MAP = {
  [core_apimessages_Payment_PaymentMethod.PAYPAL]: {
    completed: {
      hintArgs: {
        linkOpen: `<a href="${howToGetPaidPaypalArticlePath}">`,
        linkClose: '</a>',
      },
    },
    incomplete: {
      hintArgs: {
        linkOpen: `<a href="${howToGetPaidPaypalArticlePath}">`,
        linkClose: '</a>',
      },
      primaryActionText: 'editSettings',
      primaryActionPath: shopPoliciesEditPath,
    },
    incompletePayout: {
      hintArgs: {
        linkOpen: `<a href="${howToGetPaidPaypalArticlePath}">`,
        linkClose: '</a>',
      },
      primaryActionText: 'editSettings',
      primaryActionPath: shopPoliciesEditPath,
    },
  },
  [PayoutDetailsDestination.ACH]: {
    completed: {
      primaryActionText: 'viewPayout',
      primaryActionPath: myEarningsPayoutsPath,
      hintArgs: {
        linkOpen: `<a href="${howLongToGetPaidArticlePath}">`,
        linkClose: '</a>',
      },
    },
    incomplete: {
      primaryActionText: 'editSettings',
      primaryActionPath: shopPoliciesEditPath,
    },
  },
  [PayoutDetailsDestination.REVERB]: {
    completed: {
      primaryActionText: 'addPayout',
      primaryActionPath: shopPoliciesEditPath,
    },
    incomplete: {
      primaryActionText: 'addPayout',
      primaryActionPath: shopPoliciesEditPath,
    },
  },
  [PayoutDetailsDestination.WALLET]: {
    completed: {
      primaryActionText: 'viewPayout',
      primaryActionPath: myEarningsPayoutsPath,
    },
    incomplete: {
      primaryActionText: 'editSettings',
      primaryActionPath: shopPoliciesEditPath,
    },
  },
} as any;

function PayoutTypeBadge({ payoutType }: { payoutType: core_apimessages_Payment_PaymentMethod | PayoutDetailsDestination }) {
  const ICON_MAP = {
    [PayoutDetailsDestination.ACH]: BankIcon,
    [PayoutDetailsDestination.REVERB]: WarningIcon,
    [PayoutDetailsDestination.WALLET]: ReverbLogoCircleIcon,
  };

  const badgeTypeClass = classNames('payout-details__status-tag', {
    'payout-details__status-tag--paypal': payoutType === core_apimessages_Payment_PaymentMethod.PAYPAL,
    'payout-details__status-tag--ach': payoutType === PayoutDetailsDestination.ACH,
    'payout-details__status-tag--reverb': payoutType === PayoutDetailsDestination.REVERB,
    'payout-details__status-tag--wallet': payoutType === PayoutDetailsDestination.WALLET,
  });

  const normalizedPayoutType = payoutType.toString().toLowerCase();

  return (
    <div className={badgeTypeClass}>
      {normalizedPayoutType === 'paypal' ? (
        <RCImage src={PaypalLogo} imageBox="none" />
      ) : (
        <RCIcon svgComponent={ICON_MAP[payoutType]} size="inline" />
      )} {I18n.t(`selling.order.payoutDetail.payoutTypeBadge.${normalizedPayoutType}`)}
    </div>
  );
}

interface PayoutTypePrimaryActionProps {
  payoutType: core_apimessages_Payment_PaymentMethod | PayoutDetailsDestination;
  normalizedPayoutStatus: typeof COMPLETED | typeof INCOMPLETE | typeof INCOMPLETE_PAYOUT;
}

function PayoutTypePrimaryAction({
  payoutType,
  normalizedPayoutStatus,
}: PayoutTypePrimaryActionProps) {
  const primaryAction = CONTENT_MAP[payoutType][normalizedPayoutStatus];

  if (!primaryAction?.primaryActionText) return null;

  return (
    <CoreLink
      to={primaryAction?.primaryActionPath}
      className="button button--mini button--offwhite mt-4"
    >
      {I18n.t(`selling.order.payoutDetail.payoutTypePrimaryAction.${primaryAction?.primaryActionText}`)}
    </CoreLink>
  );
}

interface PayoutDetailsProps {
  orderUuid: string;
}

export default function PayoutDetails({ orderUuid }: PayoutDetailsProps) {
  const { data, loading } = useQuery<DashboardSellingOrderPayoutDetailsQuery, DashboardSellingOrderPayoutDetailsQueryVariables>(
    SELLER_ORDER_PAYOUT_DETAILS,
    {
      variables: {
        orderUuid,
      },
    },
  );

  const { payments = [], sellerOrder } = data?.order || {};
  const { payoutDetails } = sellerOrder || {};
  const { paymentStatus, paymentMethod } = payments[0] || {};
  const { status: payoutStatus, destination } = payoutDetails || {};
  const paypalPayoutsEnabled = useExpEnabled(experiments.PAYPAL_PAYOUTS);

  if (loading) {
    return null;
  }

  const payoutType = paymentMethod === core_apimessages_Payment_PaymentMethod.PAYPAL ? core_apimessages_Payment_PaymentMethod.PAYPAL : destination;

  if (!payoutType) {
    return null;
  }

  const normalizedPayoutStatus = normalizePayoutStatus(payoutType, payoutStatus, paymentStatus, paypalPayoutsEnabled);
  const hintText = `selling.order.payoutDetail.hintText.${payoutType.toString().toLowerCase()}.${normalizedPayoutStatus}`;

  return (
    <div>
      <h4 className="weight-bold mb-2">
        {I18n.t(`selling.order.payoutDetail.title.${normalizedPayoutStatus}`)}
      </h4>
      <PayoutTypeBadge payoutType={payoutType} />
      <Translate
        tag="div"
        className="hint"
        text={hintText}
        args={CONTENT_MAP[payoutType][normalizedPayoutStatus]?.hintArgs}
        html={!!CONTENT_MAP[payoutType][normalizedPayoutStatus]?.hintArgs}
      />
      <PayoutTypePrimaryAction payoutType={payoutType} normalizedPayoutStatus={normalizedPayoutStatus} />
    </div>
  );
}

export const SELLER_ORDER_PAYOUT_DETAILS = gql(`
  query DashboardSellingOrderPayoutDetails($orderUuid: String) {
    order(input: { uuid: $orderUuid }) {
      _id
      payments {
        paymentMethod
        paymentStatus
      }
      sellerOrder {
        payoutDetails {
          status
          destination
        }
      }
    }
  }
`);
